import React from 'react';
import { Link } from 'react-router-dom';
import { usePageSEO } from '../hooks/usePageSEO';
import { breadcrumbSchema } from '../utils/structuredData';
import { SparklesIcon, CameraIcon, ArrowRightIcon, ExternalLinkIcon, SearchIcon } from 'lucide-react';
export function AppsPage() {
  usePageSEO({
    canonical: '/apps',
    keywords: 'Shopify apps, FlowBoost app, AI Photoshoot app, SEO Suite app, Shopify app development',
    jsonLd: breadcrumbSchema([{
      name: 'Home',
      path: '/'
    }, {
      name: 'Apps',
      path: '/apps'
    }]),
    title: 'Apps | Design Flow Digitals',
    description: 'Explore Shopify apps by Design Flow Digitals. FlowBoost for speed optimization and AI Photoshoot for stunning product photography powered by AI.'
  });
  return <div className="pt-32 pb-20" data-id="element-779" data-mp="ei2b1eksbgbs">
      {/* Hero */}
      <section className="px-6 md:px-12 max-w-5xl mx-auto text-center mb-24" data-id="element-780" data-mp="ii2q91fpjvjw">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-8 border-[#D4AF37]/30" data-id="element-781" data-mp="ik5qeeakr52c">
          <SparklesIcon className="w-4 h-4 text-[#D4AF37]" data-id="element-782" data-mp="1f5vqk3tu0exz" />
          <span className="text-sm font-medium text-[#F3E5AB]" data-id="element-783" data-mp="1f4auuavs6pyj">
            Shopify Apps
          </span>
        </div>
        <h1 className="text-5xl md:text-7xl font-bold mb-8" data-id="element-784" data-mp="1a46cefbbbl5o">
          Smart Tools for Growth
        </h1>
        <p className="text-xl md:text-2xl text-gray-400 leading-relaxed max-w-3xl mx-auto" data-id="element-785" data-mp="22v6j4y113311">
          Powerful Shopify apps designed to improve store performance, automate
          workflows, and increase sales.
        </p>
      </section>

      {/* Categories Intro */}
      <section className="max-w-5xl mx-auto px-6 mb-20" data-id="element-786" data-mp="1sv5x34bry8t5">
        <div className="flex flex-wrap justify-center gap-4" data-id="element-787" data-mp="1nyp9cb8hlwgl">
          {['Conversion tools', 'Analytics tools', 'Automation apps', 'AI-powered tools'].map((cat, i) => <span key={i} className="px-6 py-3 rounded-full glass-card border-white/10 text-gray-300 font-medium" data-id="element-788" data-mp="ch7fc3gpkb79" data-mpi={i}>
              {cat}
            </span>)}
        </div>
      </section>

      {/* Featured Apps */}
      <section className="section-padding bg-black border-y border-white/5" data-id="element-789" data-mp="2qrb9jq93z0jp">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-790" data-mp="2vpcrg7ussttl">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-791" data-mp="2vnry2lk7zgxb">Featured Apps</h2>
          <p className="text-xl text-gray-400" data-id="element-792" data-mp="2gzx4z42avajp">
            Discover our top-rated applications built for modern merchants.
          </p>
        </div>

        <div className="flex flex-col gap-12 max-w-5xl mx-auto" data-id="element-793" data-mp="2800hv6vk6jqr">
          {/* SEO Suite App (Latest Release Highlight) */}
          <div className="glass-card p-8 md:p-12 border-emerald-500/30 bg-gradient-to-br from-emerald-900/10 to-transparent relative overflow-hidden group" data-id="element-794" data-mp="2cybwduwstifb">
            <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-[80px] pointer-events-none" data-id="element-795" data-mp="1y470na6wa22n" />

            <div className="flex flex-col md:flex-row gap-10 items-start relative z-10" data-id="element-796" data-mp="1jem21gp26u68">
              <div className="w-24 h-24 shrink-0 rounded-2xl bg-emerald-500/20 flex items-center justify-center border border-emerald-500/30 shadow-[0_0_30px_rgba(16,185,129,0.2)]" data-id="element-797" data-mp="1fdtuqszqyzcl">
                <SearchIcon className="w-12 h-12 text-emerald-400" data-id="element-798" data-mp="1tayygmbznm4o" />
              </div>

              <div className="flex-1" data-id="element-799" data-mp="1ogbdgxg719uu">
                <div className="flex items-center gap-3 mb-3" data-id="element-800" data-mp="22dhzd0w7rooa">
                  <h3 className="text-3xl font-bold text-white" data-id="element-801" data-mp="22bnbpetk5igi">SEO Suite</h3>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-bold uppercase tracking-wider" data-id="element-802" data-mp="1yc63weqns986">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" data-id="element-803" data-mp="275i18yvugrni" />{' '}
                    Latest Release
                  </span>
                </div>

                <p className="text-emerald-300 font-medium mb-4" data-id="element-804" data-mp="2h479s11uozr3">
                  Free plan available
                </p>

                <p className="text-xl text-gray-300 mb-6 leading-relaxed" data-id="element-805" data-mp="efz18cfjzz7v">
                  The all-in-one SEO solution for Shopify. Automate your
                  technical SEO, optimize images, and dominate search rankings.
                </p>

                <ul className="space-y-3 mb-8" data-id="element-806" data-mp="t5oh7dsb27s7">
                  {['Smart meta tags generation using AI', 'Automated image optimization & ALT tags', 'Technical SEO fixes and broken link management'].map((feature, i) => <li key={i} className="flex items-start gap-3 text-gray-400" data-id="element-807" data-mp="5fatu0ko0x3y" data-mpi={i}>
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2 shrink-0" data-id="element-808" data-mp="ackivf4576r9" />
                      {feature}
                    </li>)}
                </ul>

                <div className="flex flex-wrap gap-4" data-id="element-809" data-mp="1lrzfuiwcpymd">
                  <Link to="/seo-suite" className="px-8 py-3 rounded-full font-bold text-white bg-emerald-600 hover:bg-emerald-500 transition-colors shadow-[0_0_20px_rgba(16,185,129,0.3)]" data-id="element-810" data-mp="36c4jjop7herm">
                    View Details
                  </Link>
                  <a href="https://apps.shopify.com/seo-suite" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-3 rounded-full font-bold text-white border border-white/20 hover:bg-white/5 transition-colors" data-id="element-811" data-mp="2rlxa1yh6f632">
                    Install on Shopify <ExternalLinkIcon className="w-4 h-4" data-id="element-812" data-mp="28oci2ij47re8" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* AI Product Photoshoot App */}
          <div className="glass-card p-8 md:p-12 border-purple-500/30 bg-gradient-to-br from-purple-900/10 to-transparent relative overflow-hidden group" data-id="element-813" data-mp="6wzgo3gcw1mx">
            <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/10 rounded-full blur-[80px] pointer-events-none" data-id="element-814" data-mp="21bd8lkr7kokj" />

            <div className="flex flex-col md:flex-row gap-10 items-start relative z-10" data-id="element-815" data-mp="265hm6muzbuul">
              <div className="w-24 h-24 shrink-0 rounded-2xl bg-purple-500/20 flex items-center justify-center border border-purple-500/30 shadow-[0_0_30px_rgba(168,85,247,0.2)]" data-id="element-816" data-mp="1rcnum32fno9k">
                <CameraIcon className="w-12 h-12 text-purple-400" data-id="element-817" data-mp="2k47gjuqezgf9" />
              </div>

              <div className="flex-1" data-id="element-818" data-mp="2p17k9yzsk737">
                <div className="flex items-center gap-3 mb-3" data-id="element-819" data-mp="2a59vj1s76510">
                  <h3 className="text-3xl font-bold text-white" data-id="element-820" data-mp="2a4qb9ef2r4jt">
                    AI Product Photoshoot - DFD
                  </h3>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 text-xs font-bold uppercase tracking-wider" data-id="element-821" data-mp="yq304tqnuw1s">
                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" data-id="element-822" data-mp="13mtixotuo5qw" />{' '}
                    Live
                  </span>
                </div>

                <p className="text-purple-300 font-medium mb-4" data-id="element-823" data-mp="3cxcc8vniscnz">
                  Free plan available
                </p>

                <p className="text-xl text-gray-300 mb-6 leading-relaxed" data-id="element-824" data-mp="3qvizziyr8w9x">
                  Generate AI lifestyle images using your existing store product
                  photos. No studio required.
                </p>

                <ul className="space-y-3 mb-8" data-id="element-825" data-mp="2u7mimbvhtwxn">
                  {['AI-powered image generation from your catalog', 'Publish generated images directly to your products', '20+ product categories for image generation'].map((feature, i) => <li key={i} className="flex items-start gap-3 text-gray-400" data-id="element-826" data-mp="2fhopdhipih1l" data-mpi={i}>
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-2 shrink-0" data-id="element-827" data-mp="2besde9no9j6m" />
                      {feature}
                    </li>)}
                </ul>

                <div className="flex flex-wrap gap-4" data-id="element-828" data-mp="35b0izcukyqcs">
                  <Link to="/ai-photoshoot" className="px-8 py-3 rounded-full font-bold text-white bg-purple-600 hover:bg-purple-500 transition-colors shadow-[0_0_20px_rgba(147,51,234,0.3)]" data-id="element-829" data-mp="39c2lq3nphkji">
                    View Details
                  </Link>
                  <a href="https://apps.shopify.com/ai-product-photoshoot-dfd" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-3 rounded-full font-bold text-white border border-white/20 hover:bg-white/5 transition-colors" data-id="element-830" data-mp="3o8l0tzyxgonw">
                    Install on Shopify <ExternalLinkIcon className="w-4 h-4" data-id="element-831" data-mp="atx32nma1wou" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* FlowBoost App */}
          <div className="glass-card p-8 md:p-12 border-blue-500/20 bg-gradient-to-br from-blue-900/5 to-transparent relative overflow-hidden" data-id="element-832" data-mp="fztw7edc9xz8">
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/5 rounded-full blur-[80px] pointer-events-none" data-id="element-833" data-mp="b0zb25rwfxby" />

            <div className="flex flex-col md:flex-row gap-10 items-start relative z-10" data-id="element-834" data-mp="718etmjlsp01">
              <div className="w-20 h-20 shrink-0 rounded-2xl bg-blue-500/10 flex items-center justify-center border border-blue-500/20" data-id="element-835" data-mp="13r8zpaguqlc2">
                <SparklesIcon className="w-10 h-10 text-blue-400" data-id="element-836" data-mp="tyti4e9gvyrp" />
              </div>

              <div className="flex-1" data-id="element-837" data-mp="pxq662ujimvr">
                <div className="flex items-center gap-3 mb-3" data-id="element-838" data-mp="pwyide76ptg1">
                  <h3 className="text-2xl font-bold text-white" data-id="element-839" data-mp="1mfe0c927pq6n">FlowBoost</h3>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20 text-[#D4AF37] text-xs font-bold uppercase tracking-wider" data-id="element-840" data-mp="1mhh03kt62xok">
                    <span className="w-2 h-2 rounded-full bg-[#D4AF37]" data-id="element-841" data-mp="23x4znlksgb4q" />{' '}
                    Coming Soon
                  </span>
                </div>
                <p className="text-lg text-gray-300 mb-6 leading-relaxed" data-id="element-842" data-mp="2cx1eywlafxn4">
                  Boost your Shopify store's conversion rate with intelligent
                  upsell automation.
                </p>

                <ul className="space-y-3 mb-8" data-id="element-843" data-mp="2msv0h5gzj1h1">
                  {['AI product recommendations', 'Smart cart upsells', 'Analytics dashboard'].map((feature, i) => <li key={i} className="flex items-start gap-3 text-gray-400" data-id="element-844" data-mp="2rkv0sz0yvwe5" data-mpi={i}>
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 shrink-0" data-id="element-845" data-mp="35lfdrek7axzq" />
                      {feature}
                    </li>)}
                </ul>

                <Link to="/flowboost" className="inline-flex items-center gap-2 px-8 py-3 rounded-full font-bold text-white border border-white/20 hover:bg-white/5 transition-colors" data-id="element-846" data-mp="3k8e2v2vl5gba">
                  View Details <ArrowRightIcon className="w-4 h-4" data-id="element-847" data-mp="3f89njg6eyrmn" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 text-center" data-id="element-848" data-mp="31fk15amz3kp0">
        <h2 className="text-3xl md:text-5xl font-bold mb-6" data-id="element-849" data-mp="367tmedd57wky">
          Have an app idea?
        </h2>
        <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto" data-id="element-850" data-mp="2rjgo39ql21na">
          We build custom public and private Shopify apps tailored to your
          specific business needs.
        </p>
        <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-gradient-gold text-black px-10 py-4 rounded-full font-bold text-lg gold-glow-hover transition-all duration-300 hover:scale-105" data-id="element-851" data-mp="2ihh7ujne71le">
          Let's build it together <ArrowRightIcon className="w-5 h-5" data-id="element-852" data-mp="t2oirnutz75s" />
        </Link>
      </section>
    </div>;
}